import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { integrationModalTitle } from "src/features/integrations/integrationModalTitle.js";

import { useState } from "react";
import { useCreateCompanyMutation } from "src/common/redux/api/exopenApi";
import { useTranslation } from "react-i18next";
import { useSourceSystemMetadata } from "src/features/integrations/metadata";
import { AddDataSourcePage } from "../../activate/AddDataSourcePage";
import { type SubmitHandler, useForm } from "react-hook-form";
import {
	getDefaultValuesForAddDataSource,
	type AddDataSourceInputs,
} from "../../activate/schema";
import { AddDataSourceNavigation } from "../../activate/AddDataSourceNavigation";
import { AddDataSourceCompanyDomainSection } from "../../activate/AddDataSourceCompanyDomainSection";
import { ExactOnlineAuthorizationModal } from "./ExactOnlineAuthorizationModal";
import { FormSection } from "src/features/planning/components/formSection/FormSection";
import { useLoadedCompanyDomains } from "src/routes/routeDataHooks.ts";
import { useAuthorizationUrl } from "./useAuthorizationUrl";

export const ActivateExactOnlinePage = () => {
	const { t } = useTranslation();
	const companyDomains = useLoadedCompanyDomains();
	const { name, logoUrl } = useSourceSystemMetadata("EXACT_ONLINE");
	const form = useForm({
		defaultValues: getDefaultValuesForAddDataSource(
			companyDomains.length === 0 ? "new" : "add",
		),
	});

	const operation = form.watch("operation");

	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [companyDomainId, setCompanyDomainId] = useState<string | null>(null);

	const [createCompany] = useCreateCompanyMutation();
	const { onRequestAuthorizationUrl, isLoading } = useAuthorizationUrl(
		companyDomainId ?? "",
	);

	const submitHandler: SubmitHandler<AddDataSourceInputs> = async (data) => {
		if (data.operation === "add" && data.companyDomainId) {
			setCompanyDomainId(data.companyDomainId);
			setModalIsOpen(true);
			return;
		}

		if (data.operation === "new" && data.companyDomainName) {
			const result = await createCompany({
				name: data.companyDomainName,
				type: "Kund",
			});

			if ("error" in result) {
				showErrorNotification({
					message: t("Something failed..."),
				});
				return;
			}

			setCompanyDomainId(result.data.id.toString());
			setModalIsOpen(true);
		}
	};

	return (
		<form onSubmit={form.handleSubmit(submitHandler)}>
			<AddDataSourcePage title={integrationModalTitle(t, name)} logo={logoUrl}>
				<AddDataSourceNavigation
					form={form}
					onSubmit
					disableOnSubmit={
						(operation === "new" && !form.watch("companyDomainName")) ||
						(operation === "add" && !form.watch("companyDomainId"))
					}
				>
					<AddDataSourceCompanyDomainSection
						form={form}
						showWhatHappensNext={false}
						companyDomains={companyDomains}
					/>

					<FormSection title={t("What happens next?")}>
						<ol className="list-decimal">
							{operation === "add" ? (
								<li>
									{t(
										"The data source will be added to the {{name}} company domain",
										{
											name: t("selected"),
										},
									)}
								</li>
							) : (
								<>
									<li>
										{t(
											"A new company domain will be created, this will take a minute or two",
										)}
									</li>
									<li>
										{t(
											"The data source will be added to the newly created company domain",
										)}
									</li>
								</>
							)}
							<li>
								{t(
									"A data run will be started, it will take between thirty minutes to a couple of hours to complete depending on the source system and data size",
								)}
							</li>
						</ol>
					</FormSection>
				</AddDataSourceNavigation>

				{companyDomainId && (
					<ExactOnlineAuthorizationModal
						isOpen={modalIsOpen}
						onClose={() => setModalIsOpen(false)}
						title={t("Add Exact Online data source")}
						description={t(
							"You must be an administrator in Exact Online for the company you want to connect.",
						)}
						showModeSelection={true}
						isLoading={isLoading}
						onAuthorize={async (mode) => {
							const external = mode === "copy";
							const result = await onRequestAuthorizationUrl(external);
							return result ?? null;
						}}
					/>
				)}
			</AddDataSourcePage>
		</form>
	);
};
